
import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Bell, TrendingUp, MapPin, Star, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

interface ChartEntry {
  id: string;
  chartName: string;
  country: string;
  region: string;
  position: number;
  previousPosition: number | null;
  peakPosition: number;
  weeksOnChart: number;
  isNewEntry: boolean;
  isPeak: boolean;
  trackName: string;
  date: string;
}

// Mock data for demonstration purposes
const getChartAlerts = (): ChartEntry[] => {
  return [
    {
      id: "chart1",
      chartName: "Billboard Hot 100",
      country: "United States",
      region: "North America",
      position: 48,
      previousPosition: 62,
      peakPosition: 48,
      weeksOnChart: 3,
      isNewEntry: false,
      isPeak: true,
      trackName: "Summer Nights",
      date: "2023-08-12"
    },
    {
      id: "chart2",
      chartName: "Official UK Singles Chart",
      country: "United Kingdom",
      region: "Europe",
      position: 22,
      previousPosition: 35,
      peakPosition: 22,
      weeksOnChart: 4,
      isNewEntry: false,
      isPeak: true,
      trackName: "Summer Nights",
      date: "2023-08-10"
    },
    {
      id: "chart3",
      chartName: "ARIA Singles Chart",
      country: "Australia",
      region: "Oceania",
      position: 15,
      previousPosition: null,
      peakPosition: 15,
      weeksOnChart: 1,
      isNewEntry: true,
      isPeak: true,
      trackName: "Summer Nights",
      date: "2023-08-14"
    }
  ];
};

const ChartAlerts: React.FC = () => {
  const chartAlerts = getChartAlerts();
  
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle className="text-xl flex items-center gap-2">
            <Bell className="h-5 w-5 text-soundcharts-purple" />
            Chart Alerts
          </CardTitle>
          <CardDescription>Your tracks on charts worldwide</CardDescription>
        </div>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/charts" className="gap-1 text-sm">
            <span>All Charts</span>
            <ChevronRight className="h-4 w-4" />
          </Link>
        </Button>
      </CardHeader>
      
      <CardContent>
        <div className="space-y-4">
          {chartAlerts.length > 0 ? (
            chartAlerts.map((entry) => (
              <div 
                key={entry.id} 
                className="border border-border rounded-lg p-3 hover:bg-muted/30 transition-colors group"
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center gap-2">
                    <Badge 
                      variant="soundcharts-filled" 
                      className="bg-amber-600 hover:bg-amber-700 text-white flex items-center gap-1"
                    >
                      #{entry.position}
                    </Badge>
                    <h3 className="font-medium">{entry.chartName}</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="flex items-center gap-1 bg-background/50">
                      <MapPin className="h-3 w-3" />
                      {entry.country}
                    </Badge>
                    {entry.isPeak && (
                      <Badge className="bg-blue-500 hover:bg-blue-600 flex items-center gap-1">
                        <Star className="h-3 w-3" />
                        New Peak
                      </Badge>
                    )}
                    {entry.isNewEntry && (
                      <Badge className="bg-green-500 hover:bg-green-600 flex items-center gap-1">
                        <TrendingUp className="h-3 w-3" />
                        New Entry
                      </Badge>
                    )}
                  </div>
                </div>
                
                <div className="text-sm flex justify-between items-center">
                  <div>
                    <span className="text-muted-foreground">Track: </span>
                    <span className="font-medium">{entry.trackName}</span>
                  </div>
                  <div className="flex gap-4">
                    <div>
                      <span className="text-muted-foreground">Previous: </span>
                      <span>{entry.previousPosition ? `#${entry.previousPosition}` : "N/A"}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Weeks: </span>
                      <span>{entry.weeksOnChart}</span>
                    </div>
                  </div>
                </div>
                
                <div className="mt-3 flex justify-end">
                  <Button 
                    variant="link" 
                    size="sm" 
                    className="text-soundcharts-purple p-0 h-auto" 
                    asChild
                  >
                    <Link to={`/marketing/region/${entry.region.toLowerCase()}`}>
                      Create targeted marketing campaign
                    </Link>
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-6">
              <p className="text-muted-foreground">No chart entries found for your tracks.</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ChartAlerts;
